import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  EmptyState,
  Field,
  Input,
  NativeSelect,
  PageHeader,
  Skeleton,
  Switch,
  Table,
  TableShell,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@provenance/design';
import { UserRole } from '@provenance/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, PenLine, Plus, Sparkles, Upload, UserPlus, Users } from 'lucide-react';
import { useState } from 'react';
import { toBase64 } from '../features/configuration/upload';
import {
  BackgroundChoice,
  FixBackgroundDialog,
  useCleanedImage,
} from '../features/configuration/ImageBackground';
import { ApiError, api } from '../lib/api';
import { useSession } from '../lib/session';

interface ChamberUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  active: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  /** False until they follow their invitation. Decides whether a resend is offered. */
  hasPassword: boolean;
}

interface ChamberSignature {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  userActive: boolean;
  signatoryName: string;
  designation: string;
  imageUrl: string | null;
  canSign: boolean;
  isDefault: boolean;
}

interface MySignature {
  id: string;
  signatoryName: string;
  designation: string;
  imageUrl: string | null;
  isDefault: boolean;
  active: boolean;
  createdAt: string;
  /** Prints as a white box around the signature. */
  solidBackground: boolean | null;
}

/**
 * Operators (spec §7.6).
 *
 * Two things live here because they are one question in practice: who works in this
 * chamber, and which of them can actually put their name on a certificate. An operator
 * with an account but no signature image cannot satisfy a required SIGNATURE slot, so
 * the roster shows signing capability alongside the account itself.
 */
export function Operators() {
  const { user } = useSession();
  const isAdmin = user?.role === UserRole.CHAMBER_ADMIN;
  const [inviteOpen, setInviteOpen] = useState(false);

  const users = useQuery({
    queryKey: ['chamber-users'],
    queryFn: () => api.get<ChamberUser[]>('/api/console/users'),
    enabled: isAdmin,
  });
  const signatures = useQuery({
    queryKey: ['chamber-signatures'],
    queryFn: () => api.get<ChamberSignature[]>('/api/console/configuration/signatures'),
    enabled: isAdmin,
  });

  const signaturesByUser = new Map<string, ChamberSignature[]>();
  for (const sig of signatures.data ?? []) {
    const list = signaturesByUser.get(sig.userId) ?? [];
    list.push(sig);
    signaturesByUser.set(sig.userId, list);
  }

  const activeUsers = (users.data ?? []).filter((u) => u.active);
  const cannotSign = activeUsers.filter(
    (u) => !(signaturesByUser.get(u.id) ?? []).some((s) => s.canSign),
  );

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Operators"
        lede="The people in your chamber who review and issue certificates. Nobody is ever emailed a password — an invitation is a single-use link on which they set their own."
        actions={
          isAdmin && (
            <Button variant="primary" leadingIcon={<UserPlus className="size-3.5" />} onClick={() => setInviteOpen(true)}>
              Invite an operator
            </Button>
          )
        }
      />

      <MySignatures />

      {isAdmin && (
        <>
          {cannotSign.length > 0 && (
            <Callout
              tone="warn"
              icon={<AlertTriangle className="size-4" />}
              title={`${cannotSign.length} active operator${cannotSign.length === 1 ? ' has' : 's have'} no signature image`}
            >
              {cannotSign.map((u) => `${u.firstName} ${u.lastName}`).join(', ')} can review
              submissions but cannot approve a document type whose layout requires a signature.
              Each operator adds their own — a signature is a personal attestation and nobody
              else can upload it for them.
            </Callout>
          )}

          <Card>
            <CardHeader
              title="Everyone in this chamber"
              hint={users.data ? `${users.data.length}` : undefined}
              description="Deactivating an operator revokes their sessions immediately, not at token expiry."
            />
            {users.isLoading ? (
              <Skeleton className="h-48" />
            ) : (users.data ?? []).length === 0 ? (
              <EmptyState
                icon={<Users className="size-5" />}
                title="Only you, so far"
                description="Invite the colleagues who will review and issue certificates."
              />
            ) : (
              <TableShell>
                <Table className="min-w-[760px]">
                  <Thead>
                    <Tr>
                      <Th>Name</Th>
                      <Th>Role</Th>
                      <Th>Can sign</Th>
                      <Th>Last signed in</Th>
                      <Th>Active</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(users.data ?? []).map((row) => (
                      <UserRow
                        key={row.id}
                        row={row}
                        signatures={signaturesByUser.get(row.id) ?? []}
                        isSelf={row.id === user?.id}
                      />
                    ))}
                  </Tbody>
                </Table>
              </TableShell>
            )}
          </Card>
        </>
      )}

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  );
}

function UserRow({
  row,
  signatures,
  isSelf,
}: {
  row: ChamberUser;
  signatures: ChamberSignature[];
  isSelf: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const signable = signatures.find((s) => s.canSign);

  /**
   * An invitation lasts seven days and `invite()` calls `user.create`, so once a link
   * expires the email address is taken and that person could never be invited again. This
   * is the way back, and the only one short of the database.
   */
  const resend = useMutation({
    mutationFn: () => api.post(`/api/console/users/${row.id}/resend-invitation`),
    onSuccess: () =>
      toast.success('Invitation sent again', `${row.email} has a fresh link, good for seven days.`),
    onError: (error) =>
      toast.error('Could not send the invitation', error instanceof ApiError ? error.message : ''),
  });

  /** Sends a link. An administrator never types another person's password. */
  const sendReset = useMutation({
    mutationFn: () => api.post(`/api/console/users/${row.id}/reset-password`),
    onSuccess: () =>
      toast.success('Reset link sent', `${row.email} can set a new password. The link lasts two hours.`),
    onError: (error) =>
      toast.error('Could not send the link', error instanceof ApiError ? error.message : ''),
  });

  const changeRole = useMutation({
    mutationFn: (role: UserRole) => api.patch(`/api/console/users/${row.id}`, { role }),
    onSuccess: (_r, role) => {
      toast.success(
        role === UserRole.CHAMBER_ADMIN ? 'Made an administrator' : 'Changed to operator',
        `${row.firstName} ${row.lastName} — recorded in the audit trail.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['chamber-users'] });
    },
    onError: (error) =>
      // The last-administrator refusal arrives here, and its message explains itself.
      toast.error('Could not change the role', error instanceof ApiError ? error.message : ''),
  });

  const setActive = useMutation({
    mutationFn: (active: boolean) => api.patch(`/api/console/users/${row.id}/active`, { active }),
    onSuccess: (_r, active) => {
      toast.success(
        active ? 'Operator reactivated' : 'Operator deactivated',
        active ? undefined : 'Their sessions have been revoked.',
      );
      void queryClient.invalidateQueries({ queryKey: ['chamber-users'] });
    },
    onError: (error) =>
      toast.error('Could not change the account', error instanceof ApiError ? error.message : ''),
  });

  return (
    <Tr>
      <Td>
        <span className="font-medium">
          {row.firstName} {row.lastName}
          {isSelf && <span className="ml-1.5 [font-size:11px] text-[var(--pv-faint)]">(you)</span>}
        </span>
        <span className="mt-0.5 block [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
          {row.email}
        </span>
      </Td>
      <Td>
        {isSelf ? (
          // Changing your own role is how somebody accidentally demotes the last
          // administrator — themselves. The server refuses it; the screen does not offer it.
          <Badge tone={row.role === UserRole.CHAMBER_ADMIN ? 'info' : 'neutral'} size="sm">
            {row.role === UserRole.CHAMBER_ADMIN ? 'Chamber admin' : 'Operator'}
          </Badge>
        ) : (
          <NativeSelect
            value={row.role}
            disabled={changeRole.isPending}
            aria-label={`Role for ${row.firstName} ${row.lastName}`}
            onChange={(e) => changeRole.mutate(e.target.value as UserRole)}
          >
            <option value={UserRole.CHAMBER_OPERATOR}>Operator</option>
            <option value={UserRole.CHAMBER_ADMIN}>Chamber admin</option>
          </NativeSelect>
        )}
      </Td>
      <Td>
        {signable ? (
          <Tooltip content={`${signable.signatoryName} — ${signable.designation}`}>
            <span>
              <Badge tone="ok" size="sm" dot>Yes</Badge>
            </span>
          </Tooltip>
        ) : (
          <Badge tone="warn" size="sm">No signature</Badge>
        )}
      </Td>
      <Td>
        {row.lastLoginAt ? (
          <span className="pv-num [font-size:12.5px] text-[var(--pv-muted)]">
            {new Date(row.lastLoginAt).toLocaleDateString('en-AU', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </span>
        ) : (
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="[font-size:12.5px] text-[var(--pv-muted)]">
              {row.hasPassword ? 'Never signed in' : 'Invitation not yet accepted'}
            </span>
            {!row.hasPassword && (
              <Button
                variant="ghost"
                size="sm"
                loading={resend.isPending}
                onClick={() => resend.mutate()}
              >
                Send again
              </Button>
            )}
          </span>
        )}
      </Td>
      <Td>
        {isSelf ? (
          // A disabled switch reads as "off" at a glance, which is exactly the wrong
          // thing to say about the account you are signed in with. The server refuses
          // this anyway — locking yourself out is a support call nobody enjoys.
          <Tooltip content="You cannot deactivate your own account">
            <span>
              <Badge tone="ok" size="sm" dot>Active</Badge>
            </span>
          </Tooltip>
        ) : (
          <span className="flex items-center gap-2">
            {row.active && row.hasPassword && (
              <Button variant="ghost" size="sm" loading={sendReset.isPending} onClick={() => sendReset.mutate()}>
                Send reset
              </Button>
            )}
            <Switch
              checked={row.active}
              disabled={setActive.isPending}
              onCheckedChange={(active) => setActive.mutate(active)}
              aria-label={`${row.active ? 'Deactivate' : 'Activate'} ${row.firstName} ${row.lastName}`}
            />
          </span>
        )}
      </Td>
    </Tr>
  );
}

/** Every operator manages their own, including a Chamber Admin. */
export function MySignatures() {
  const [editing, setEditing] = useState<MySignature | 'new' | null>(null);
  const [fixing, setFixing] = useState<MySignature | null>(null);
  const toast = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['my-signatures'],
    queryFn: () => api.get<MySignature[]>('/api/console/configuration/signatures/mine'),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => api.post(`/api/console/configuration/signatures/${id}/deactivate`, {}),
    onSuccess: () => {
      toast.success('Signature deactivated');
      void queryClient.invalidateQueries({ queryKey: ['my-signatures'] });
      void queryClient.invalidateQueries({ queryKey: ['chamber-signatures'] });
    },
    onError: (error) =>
      toast.error('Could not deactivate it', error instanceof ApiError ? error.message : ''),
  });

  const rows = query.data ?? [];

  return (
    <Card>
      <CardHeader
        title="Your signature"
        description="What the stamping engine prints when you approve a certificate. Yours alone — no administrator can create or change it for you."
        actions={
          <Button variant="secondary" size="sm" leadingIcon={<Plus className="size-3.5" />} onClick={() => setEditing('new')}>
            Add a signature
          </Button>
        }
      />

      {query.isLoading ? (
        <Skeleton className="h-28" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<PenLine className="size-5" />}
          title="You have no signature yet"
          description="Until you add one, you cannot approve a document type whose layout requires a signature."
          action={
            <Button variant="primary" onClick={() => setEditing('new')}>
              Add your signature
            </Button>
          }
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {rows.map((sig) => (
            <div
              key={sig.id}
              className={
                'flex items-start gap-3 rounded-[var(--pv-radius)] border border-[var(--pv-line)] p-3 ' +
                (sig.active ? '' : 'opacity-60')
              }
            >
              <div className="grid h-16 w-28 shrink-0 place-items-center rounded-[var(--pv-radius-sm)] border border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] p-1.5">
                {sig.imageUrl ? (
                  <img src={sig.imageUrl} alt="" className="max-h-full max-w-full object-contain" />
                ) : (
                  <span className="[font-size:11px] text-[var(--pv-faint)]">No image</span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{sig.signatoryName}</p>
                <p className="truncate [font-size:12.5px] text-[var(--pv-muted)]">{sig.designation}</p>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {sig.isDefault && <Badge tone="info" size="sm">Default</Badge>}
                  {!sig.imageUrl && <Badge tone="warn" size="sm">Cannot sign</Badge>}
                  {sig.solidBackground && (
                    <Badge tone="warn" size="sm" dot>Solid background</Badge>
                  )}
                  {!sig.active && <Badge tone="neutral" size="sm">Inactive</Badge>}
                </div>
                <div className="mt-2 flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(sig)}>
                    Edit
                  </Button>
                  {sig.solidBackground && (
                    <Button
                      variant="ghost"
                      size="sm"
                      leadingIcon={<Sparkles className="size-3.5" />}
                      onClick={() => setFixing(sig)}
                    >
                      Make transparent
                    </Button>
                  )}
                  {sig.active && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[var(--pv-stop)]"
                      onClick={() => deactivate.mutate(sig.id)}
                    >
                      Deactivate
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <SignatureDialog
          signature={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      {fixing && (
        <FixBackgroundDialog
          open
          onOpenChange={(open) => !open && setFixing(null)}
          what="signature"
          name={fixing.signatoryName}
          load={() => api.blob(`/api/console/configuration/signatures/${fixing.id}/image`)}
          save={async (file) => {
            await api.patch(`/api/console/configuration/signatures/${fixing.id}`, {
              filename: file.name,
              contentBase64: await toBase64(file),
            });
          }}
          onSaved={() => {
            toast.success('Signature updated', 'Your next certificate uses the transparent version.');
            void queryClient.invalidateQueries({ queryKey: ['my-signatures'] });
            void queryClient.invalidateQueries({ queryKey: ['chamber-signatures'] });
          }}
        />
      )}
    </Card>
  );
}

function SignatureDialog({
  signature,
  onClose,
}: {
  signature: MySignature | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [signatoryName, setSignatoryName] = useState(signature?.signatoryName ?? '');
  const [designation, setDesignation] = useState(signature?.designation ?? '');
  const [isDefault, setIsDefault] = useState(signature?.isDefault ?? false);
  const [file, setFile] = useState<File | null>(null);
  const [useCleaned, setUseCleaned] = useState(true);
  const cleaned = useCleanedImage(file, file?.name ?? 'signature.png');
  /** The transparent version unless its owner declined it. */
  const upload = cleaned?.state === 'solid' && useCleaned ? cleaned.cleaned : file;

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        signatoryName,
        designation,
        isDefault,
        ...(upload ? { filename: upload.name, contentBase64: await toBase64(upload) } : {}),
      };
      return signature
        ? api.patch<{ warning: string | null }>(`/api/console/configuration/signatures/${signature.id}`, body)
        : api.post<{ warning: string | null }>('/api/console/configuration/signatures', body);
    },
    onSuccess: (result) => {
      if (result?.warning) toast.warn(signature ? 'Signature updated, with a caveat' : 'Signature added, with a caveat', result.warning);
      else toast.success(signature ? 'Signature updated' : 'Signature added');
      void queryClient.invalidateQueries({ queryKey: ['my-signatures'] });
      void queryClient.invalidateQueries({ queryKey: ['chamber-signatures'] });
      onClose();
    },
    onError: (error) =>
      toast.error('Could not save the signature', error instanceof ApiError ? error.message : ''),
  });

  const previewUrl = upload ? URL.createObjectURL(upload) : signature?.imageUrl;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title={signature ? 'Edit your signature' : 'Add your signature'}
          description="A scanned signature on a transparent PNG. It is composited onto the certificate at the position your chamber's layout defines."
        />
        <DialogBody>
          <div className="flex flex-col gap-3.5">
            <Field label="Signature image" required={!signature}>
              <div className="flex items-center gap-3">
                <div className="grid h-16 w-28 shrink-0 place-items-center rounded-[var(--pv-radius-sm)] border border-dashed border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] p-1.5">
                  {previewUrl ? (
                    <img src={previewUrl} alt="" className="max-h-full max-w-full object-contain" />
                  ) : (
                    <Upload aria-hidden className="size-5 text-[var(--pv-faint)]" />
                  )}
                </div>
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  className="[font-size:13px] file:mr-3 file:rounded-[var(--pv-radius-sm)] file:border file:border-[var(--pv-line)] file:bg-[var(--pv-paper)] file:px-3 file:py-1.5 file:[font-size:13px]"
                  aria-label="Choose a signature image"
                />
              </div>
            </Field>

            {cleaned?.state === 'solid' && (
              <BackgroundChoice result={cleaned} useCleaned={useCleaned} onChange={setUseCleaned} what="signature" />
            )}

            <Field label="Name as it appears on the certificate" required>
              <Input
                value={signatoryName}
                onChange={(e) => setSignatoryName(e.target.value)}
                placeholder="J. A. Whitlam"
              />
            </Field>

            <Field label="Designation" required help="Printed beneath your name.">
              <Input
                value={designation}
                onChange={(e) => setDesignation(e.target.value)}
                placeholder="Authorised Signatory"
              />
            </Field>

            <Field label="Use by default" help="Pre-selected when you approve a certificate.">
              <NativeSelect
                value={isDefault ? 'yes' : 'no'}
                onChange={(e) => setIsDefault(e.target.value === 'yes')}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </NativeSelect>
            </Field>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={
              !signatoryName.trim() || !designation.trim() || (!signature && !file) || cleaned?.state === 'checking'
            }
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            {signature ? 'Save changes' : 'Add signature'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<UserRole>(UserRole.CHAMBER_OPERATOR);

  const invite = useMutation({
    mutationFn: () => api.post('/api/console/users/invite', { email, firstName, lastName, role }),
    onSuccess: () => {
      toast.success('Invitation sent', `${firstName} will set their own password from the link.`);
      void queryClient.invalidateQueries({ queryKey: ['chamber-users'] });
      onOpenChange(false);
      setEmail('');
      setFirstName('');
      setLastName('');
    },
    onError: (error) =>
      toast.error('Could not send the invitation', error instanceof ApiError ? error.message : ''),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          title="Invite an operator"
          description="They receive a single-use link and choose their own password. No password is ever sent by email."
        />
        <DialogBody>
          <div className="flex flex-col gap-3.5">
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name" required>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </Field>
              <Field label="Last name" required>
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </Field>
            </div>
            <Field label="Email" required>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field
              label="Role"
              help="A chamber admin can also invite colleagues and change configuration."
            >
              <NativeSelect value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
                <option value={UserRole.CHAMBER_OPERATOR}>Operator — reviews and issues</option>
                <option value={UserRole.CHAMBER_ADMIN}>Chamber admin — and configures</option>
              </NativeSelect>
            </Field>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!email.trim() || !firstName.trim() || !lastName.trim()}
            loading={invite.isPending}
            onClick={() => invite.mutate()}
          >
            Send invitation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The operator's own signature, on its own page.
 *
 * Configuration is Chamber Admin only, but a signature is not configuration — it is a
 * personal attestation, and an operator without one cannot approve a certificate at all.
 * So it lives here, reachable by everyone, rather than behind a door they cannot open.
 */
export function MySignaturePage() {
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="My signature"
        lede="Your signature image, the name and the designation that appear on every certificate you issue. Yours alone — no administrator can create or change it for you."
      />
      <MySignatures />
    </div>
  );
}
